import { View, StyleSheet, Text } from "react-native";
import { useCallback, useEffect, useMemo, useState, type DragEvent } from "react";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import { Bot } from "lucide-react";
import { Info as InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import { Upload } from "lucide-react";
import AdminCompanionDocumentCardList from "../../components/adminDashboard/AdminCompanionDocumentCardList/AdminCompanionDocumentCardList";
import {
  DocumentStatus as ApiDocumentStatus,
  StoragePathType,
  useGetDocumentLazyQuery,
  useDeleteAdminDocumentMutation,
  useGetAdminDocumentsForTenantQuery,
  useCreateAdminDocumentMutation,
  useUpdateAdminDocumentMutation,
} from "@axio/graphql/generated/graphql";
import useFileUpload, { ProcessedFile } from "../../hooks/useFileUpload";
import { formatSubmittedDate } from "../../utils/date";
import { DocumentData } from "../../components/adminDashboard/AdminCompanionDocumentCard/AdminCompanionDocumentCard";
import { AxioActionModal } from "../../components/axio-ds/Modal/AxioModal";
import { colors, spacing, radii, typographyScale } from "../../styles/design-system";

const POLL_INTERVAL_MS = 4000;

const mapApiStatusToUi = (status: ApiDocumentStatus): DocumentData["status"] => {
  switch (status) {
    case ApiDocumentStatus.Completed:
      return "ready";
    case ApiDocumentStatus.Failed:
      return "failed";
    case ApiDocumentStatus.Pending:
    case ApiDocumentStatus.Processing:
    default:
      return "processing";
  }
};

const mapAdminDocumentToCard = (document: {
  id: string;
  title?: string | null;
  status: ApiDocumentStatus;
  isPolicy?: boolean | null;
  created?: string | null;
  files?: Array<{ id: string; fileName?: string | null }> | null;
}): DocumentData => {
  const primaryFile = document.files?.[0];
  return {
    id: document.id,
    documentId: document.id,
    fileId: primaryFile?.id,
    filename: primaryFile?.fileName || document.title || "Document",
    size: "Unknown size",
    uploadedDate: formatSubmittedDate(document.created),
    status: mapApiStatusToUi(document.status),
    isPolicy: document.isPolicy ?? false,
  };
};

function AdminCompanionSettingsContainer() {
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [isUploadHovered, setIsUploadHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [confirmAction, setConfirmAction] = useState<{
    type: "delete" | "removePolicy";
    id: string;
    filename: string;
  } | null>(null);

  const [createAdminDocument] = useCreateAdminDocumentMutation();
  const [deleteAdminDocument] = useDeleteAdminDocumentMutation();
  const [updateAdminDocument] = useUpdateAdminDocumentMutation();
  const [getDocument] = useGetDocumentLazyQuery({
    fetchPolicy: "network-only",
  });
  const { data: adminDocumentsData, refetch: refetchAdminDocuments } = useGetAdminDocumentsForTenantQuery({
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    setDocuments(
      (adminDocumentsData?.getAdminDocumentsForTenant?.data || []).map((document) => mapAdminDocumentToCard(document))
    );
  }, [adminDocumentsData]);

  const handleAfterUpload = useCallback(
    async (processedFiles: ProcessedFile[]) => {
      const file = processedFiles?.[0];
      if (!file) return;

      const displayName = file.file.fileName || "Document";

      await refetchAdminDocuments();

      try {
        const result = await createAdminDocument({
          variables: {
            fileId: file.file.id,
            title: displayName,
          },
        });

        const created = result.data?.createAdminDocument?.data;
        if (!result.data?.createAdminDocument?.success || !created?.id) {
          throw new Error(result.data?.createAdminDocument?.message || "Failed to create admin document.");
        }
        await refetchAdminDocuments();
      } catch (error) {
        const message = error instanceof Error ? error.message : "Upload failed.";
        setUploadError(message);
      }
    },
    [createAdminDocument, refetchAdminDocuments]
  );

  const { handleFileUpload, handleServerUpload, allowedMimeTypes, maxFileSize } = useFileUpload({
    storagePath: StoragePathType.AdminDocuments,
    multiple: false,
    beforeUpload: () => setUploadError(""),
    afterUpload: handleAfterUpload,
    onError: (message) => setUploadError(message || "Upload failed."),
  });

  const handleAddFile = useCallback(async () => {
    try {
      await handleFileUpload();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload failed.";
      setUploadError(message);
    }
  }, [handleFileUpload]);

  const handleDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setUploadError("");

      const droppedFile = e.dataTransfer?.files?.[0];
      if (!droppedFile) return;

      const { type: mimeType, size, name } = droppedFile;

      // Validate file constraints
      const isTypeOk = !allowedMimeTypes?.length || allowedMimeTypes.includes(mimeType);
      if (!isTypeOk) {
        setUploadError(`Invalid file type. Accepted types: PDF, DOCX, TXT.`);
        return;
      }
      if (maxFileSize && size > maxFileSize) {
        setUploadError(`File size must be under ${maxFileSize / 1024 / 1024}MB.`);
        return;
      }

      try {
        const uri = URL.createObjectURL(droppedFile);
        const result = await handleServerUpload({ uri, mimeType, size, name });
        if (result) {
          // Web drag-and-drop provides a browser File; cast to the upload helper's expected type.
          const localUploadedFile = droppedFile as ProcessedFile["localUploadedFile"];
          await handleAfterUpload([
            { file: result.file, signedUrl: result.signedUrl, size, mimeType, localUploadedFile },
          ]);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Upload failed.";
        setUploadError(message);
      }
    },
    [handleServerUpload, handleAfterUpload, allowedMimeTypes, maxFileSize]
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const documentsToPoll = useMemo(
    () => documents.filter((doc) => doc.documentId && doc.status !== "ready" && doc.status !== "failed"),
    [documents]
  );

  useEffect(() => {
    if (documentsToPoll.length === 0) return;
    let isActive = true;

    const pollStatuses = async () => {
      const results = await Promise.all(
        documentsToPoll.map(async (doc) => {
          try {
            const response = await getDocument({ variables: { id: doc.documentId } });
            return { documentId: doc.documentId, data: response.data?.getDocument?.data };
          } catch {
            return { documentId: doc.documentId, data: null };
          }
        })
      );

      if (!isActive) return;

      await refetchAdminDocuments();
    };

    pollStatuses();
    const interval = setInterval(pollStatuses, POLL_INTERVAL_MS);
    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [documentsToPoll, getDocument]);

  const handleDeleteRequest = useCallback(
    (id: string) => {
      const doc = documents.find((d) => d.id === id);
      setConfirmAction({ type: "delete", id, filename: doc?.filename ?? "this document" });
    },
    [documents]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === id
            ? {
                ...doc,
                status: "deleting",
                statusText: "Deleting...",
              }
            : doc
        )
      );

      try {
        const result = await deleteAdminDocument({ variables: { id } });
        if (!result.data?.deleteAdminDocument?.success) {
          throw new Error(result.data?.deleteAdminDocument?.message || "Failed to delete document.");
        }
        setDocuments((prev) => prev.filter((doc) => doc.id !== id));
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to delete document.";
        setUploadError(message);
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === id
              ? {
                  ...doc,
                  status: "ready",
                  statusText: undefined,
                }
              : doc
          )
        );
      }
    },
    [deleteAdminDocument, documents]
  );

  const handleTogglePolicy = useCallback(
    async (id: string, isPolicy: boolean) => {
      // Optimistically update the UI
      setDocuments((prev) => prev.map((doc) => (doc.id === id ? { ...doc, isPolicy } : doc)));

      try {
        const result = await updateAdminDocument({
          variables: { id, isPolicy },
        });
        if (!result.data?.updateAdminDocument?.success) {
          throw new Error(result.data?.updateAdminDocument?.message || "Failed to update document.");
        }
      } catch (error) {
        // Revert on failure
        setDocuments((prev) => prev.map((doc) => (doc.id === id ? { ...doc, isPolicy: !isPolicy } : doc)));
        const message = error instanceof Error ? error.message : "Failed to update document.";
        setUploadError(message);
      }
    },
    [updateAdminDocument]
  );

  const handleTogglePolicyRequest = useCallback(
    (id: string, isPolicy: boolean) => {
      if (!isPolicy) {
        // Removing policy — confirm first
        const doc = documents.find((d) => d.id === id);
        setConfirmAction({ type: "removePolicy", id, filename: doc?.filename ?? "this document" });
        return;
      }
      // Adding policy — no confirmation needed
      handleTogglePolicy(id, isPolicy);
    },
    [documents, handleTogglePolicy]
  );

  const handleConfirmAction = useCallback(() => {
    if (!confirmAction) return;
    if (confirmAction.type === "delete") {
      handleDelete(confirmAction.id);
    } else {
      handleTogglePolicy(confirmAction.id, false);
    }
    setConfirmAction(null);
  }, [confirmAction, handleDelete, handleTogglePolicy]);

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        {/* Header Section */}
        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <Bot size={20} color="#7C3AED" />
          </View>
          <View style={styles.headerContent}>
            <CustomText
              text="Knowledge Base"
              size="xxl"
              weight="bold"
              useTranslationText={false}
              style={styles.title}
            />
            <CustomSpacing type="vertical" size="xs" />
            <CustomText
              text="Manage the documents that power the AI Companion's knowledge base."
              size="s"
              useTranslationText={false}
              style={styles.description}
            />
          </View>
        </View>

        <CustomSpacing type="vertical" size="l" />

        {/* Upload Area */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload documents for the AI Knowledge Base. Accepts PDF, DOCX, TXT files up to 25MB each."
          className="upload-area"
          style={{
            border: isDragging || isUploadHovered ? "2px dashed #BF84FC" : "2px dashed #D1D5DB",
            borderRadius: 8,
            padding: 32,
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: isDragging || isUploadHovered ? "#faf5ff80" : "#FFFFFF",
            transition: "border-color 0.2s, background-color 0.2s",
          }}
          onClick={handleAddFile}
          onMouseEnter={() => setIsUploadHovered(true)}
          onMouseLeave={() => setIsUploadHovered(false)}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onKeyPress={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleAddFile();
            }
          }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <Upload size={32} color="#9CA3AF" />
            <div>
              <View style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <CustomText
                  text="Drop files here or click to upload"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={{ color: "#374151", marginBottom: 4 }}
                />
                <CustomText
                  text="PDF, DOCX, TXT up to 25MB each"
                  size="xs"
                  useTranslationText={false}
                  style={{ color: "#6B7280" }}
                />
              </View>
            </div>
          </div>
        </div>

        <CustomSpacing type="vertical" size="l" />

        {uploadError ? (
          <>
            <CustomText text={uploadError} size="xs" useTranslationText={false} style={styles.errorText} />
            <CustomSpacing type="vertical" size="s" />
          </>
        ) : null}

        <CustomSpacing type="vertical" size="l" />

        {/* Document List */}
        <AdminCompanionDocumentCardList
          documents={documents}
          onDelete={handleDeleteRequest}
          onTogglePolicy={handleTogglePolicyRequest}
        />

        <CustomSpacing type="vertical" size="l" />

        {/* Help Card */}
        <View style={styles.helpCard}>
          <InfoIcon size={20} color="rgb(7, 89, 133)" weight="regular" style={styles.helpIcon} />
          <View style={styles.helpContent}>
            <CustomText
              text="How the Knowledge Base Works"
              size="m"
              weight="bold"
              useTranslationText={false}
              style={styles.helpTitle}
            />
            <CustomSpacing type="vertical" size="xs" />
            <View style={styles.helpList}>
              <CustomText
                text="• Uploaded documents are processed and ready to use within about a minute"
                size="s"
                useTranslationText={false}
                style={styles.helpListItem}
              />
              <CustomText
                text="• Students can ask the AI Companion questions about your uploaded content, such as handbooks, FAQs, or guidelines"
                size="s"
                useTranslationText={false}
                style={styles.helpListItem}
              />
              <CustomText
                text="• Documents marked as Policy are always applied — the AI will follow your institution's rules in every response"
                size="s"
                useTranslationText={false}
                style={styles.helpListItem}
              />
            </View>
          </View>
        </View>
      </View>

      <AxioActionModal
        visible={confirmAction?.type === "delete"}
        onRequestClose={() => setConfirmAction(null)}
        title="Delete Document"
        primaryActionLabel="Delete"
        onPrimaryAction={handleConfirmAction}
        secondaryActionLabel="Cancel"
        onSecondaryAction={() => setConfirmAction(null)}
        ariaLabel="Delete Document Confirmation">
        <Text style={styles.confirmText}>
          Are you sure you want to delete &quot;{confirmAction?.filename}&quot;? This action cannot be undone.
        </Text>
      </AxioActionModal>

      <AxioActionModal
        visible={confirmAction?.type === "removePolicy"}
        onRequestClose={() => setConfirmAction(null)}
        title="Remove Policy Enforcement"
        primaryActionLabel="Remove Policy"
        onPrimaryAction={handleConfirmAction}
        secondaryActionLabel="Cancel"
        onSecondaryAction={() => setConfirmAction(null)}
        ariaLabel="Remove Policy Confirmation">
        <Text style={styles.confirmText}>
          &quot;{confirmAction?.filename}&quot; will no longer be automatically enforced in AI Companion responses. It
          will still be available as a searchable document.
        </Text>
      </AxioActionModal>
    </View>
  );
}

const styles = StyleSheet.create({
  confirmText: {
    fontSize: 14,
    color: colors.grey[600],
    textAlign: "center",
    lineHeight: 20,
  },
  container: {
    flex: 1,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 24,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 16,
    backgroundColor: "#F3E8FF",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    color: "#111827",
  },
  description: {
    color: "#475467",
  },
  helpCard: {
    backgroundColor: colors.blue[100],
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.blue[500],
    padding: spacing.lg,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  helpIcon: {
    marginTop: 2,
    flexShrink: 0,
  },
  helpContent: {
    flex: 1,
  },
  helpTitle: {
    color: colors.grey[900],
  },
  helpList: {
    gap: 6,
  },
  helpListItem: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "400",
  },
  errorText: {
    color: "#B91C1C",
  },
});

export default AdminCompanionSettingsContainer;
